import { Box, IconButton, Typography, useTheme } from '@material-ui/core'
import TextField from '@material-ui/core/TextField'
import { VisualArticle } from 'components/domain/stockPricing/StockDisplay/VisualArticle'
import { CustomTooltip, HotkeyLegend, RestartIcon } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { useAppContext, useHotkeyConfig, useScopelessHotkeys, useWindowDimensions } from 'lib/hooks'
import { useSnackbar } from 'notistack'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import Draggable from 'react-draggable'
import { useTranslation } from 'react-i18next'
import {
  CardDataItem,
  CardLanguage,
  CountryEdition,
  FINISH_ATTRIBUTES,
  FINISH_TYPE_LABELS,
  InventoryArticle,
  RichArticle,
  TAXATION_SCHEME,
  Template,
} from 'shared'
import { initializeFinishTypeAccordingCardNameOrFoil } from 'utils/lib'
import { v4 as uuid } from 'uuid'

export type ArticleAddHandleSave = (
  article: InventoryArticle | 'previous' | 'next',
  isCopied?: boolean
) => void

interface ArticleAddProps {
  addByExp?: boolean
  disableHotkeys: boolean
  template: Template
  card: CardDataItem
  handleSave: ArticleAddHandleSave
  stopEdit: () => void
  onInputChange?: (newValue: RichArticle | null) => void
  showExistingStock?: boolean
  fixedLegendPosition?: [x: number, y: number]
}

export const ArticleAdd = ({
  addByExp,
  disableHotkeys,
  template,
  card,
  handleSave,
  stopEdit,
  onInputChange,
  showExistingStock = true,
  fixedLegendPosition,
}: ArticleAddProps): JSX.Element => {
  const { isMobile, isPhone } = useAppContext()
  const { t } = useTranslation()
  const hotkeys = useHotkeyConfig()
  const { enqueueSnackbar } = useSnackbar()

  const { palette } = useTheme()

  const [draggableRerenderKey, setDraggableRerenderKey] = useState(uuid())

  const textFieldRef = useRef<HTMLInputElement | undefined>()

  const [hasFocus, setHasFocus] = useState(false)
  const [gradingActive, setGradingActive] = useState(false)
  const [articleIsGraded, setArticleIsGraded] = useState(false)

  const { user } = useUser()
  const templateValues = template

  const { width } = useWindowDimensions()

  const initialInputs = new RichArticle({
    card,
    ...templateValues,
    finishType:
      template.finishType !== FINISH_TYPE_LABELS.REGULAR
        ? template.finishType
        : initializeFinishTypeAccordingCardNameOrFoil(card),
    idArticle: templateValues.id ?? uuid(),
  })

  const [inputs, setInputs] = useState<RichArticle>(initialInputs)
  const [disabledCommentTextfield, setDisabledCommentTextfield] = useState(false)
  const [disabledQuantityTextfield, setDisabledQuantityTextfield] = useState(false)

  useEffect(() => {
    if (JSON.stringify(inputs) !== JSON.stringify(initialInputs)) {
      setInputs(initialInputs)
      onInputChange?.(initialInputs)
    }
  }, [card])

  useEffect(() => {
    textFieldRef.current?.focus()
  }, [])

  useEffect(() => {
    if (articleIsGraded) {
      setDisabledQuantityTextfield(true)
      const newInputs = inputs.copy()

      newInputs.setAttribute('quantity', 1)

      setInputs(newInputs)
    }
  }, [articleIsGraded])

  // put focus to quantity textfield when grading panel will closed
  useEffect(() => {
    if (gradingActive) {
      textFieldRef.current?.blur()
    } else {
      textFieldRef.current?.focus()
    }
  }, [gradingActive])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): Promise<void> | void => {
    const newInputs = inputs.copy()

    newInputs.setAttribute(
      e.target.name,
      e.target.type === 'checkbox' ? e.target.checked : e.target.value
    )

    onInputChange?.(newInputs)
    setInputs(newInputs)
  }

  const hotkeyCallback = (e: KeyboardEvent, fn: () => void, force = false): void => {
    if ((!disableHotkeys && document.activeElement === textFieldRef.current) || force) {
      e.preventDefault()
      e.stopPropagation()
      fn()
    }
  }

  const set = (attribute: keyof typeof template, rawValue: string | number | boolean): void => {
    if (attribute === 'taxationScheme') {
      const scheme = Number(rawValue) as TAXATION_SCHEME
      const newInputs = inputs.copy()

      // set the right quantities based on the selected scheme
      if (scheme === TAXATION_SCHEME.STANDARD) {
        newInputs.standardTaxationQuantity = newInputs.quantity
        newInputs.marginTaxationQuantity = 0
      } else {
        newInputs.marginTaxationQuantity = newInputs.quantity
        newInputs.standardTaxationQuantity = 0
      }

      // Update status and return
      onInputChange?.(newInputs)
      setInputs(newInputs)
      return
    }

    if (
      initializeFinishTypeAccordingCardNameOrFoil(card) !== FINISH_TYPE_LABELS.REGULAR &&
      //@ts-ignore
      FINISH_ATTRIBUTES.includes(attribute.replace('is', ''))
    ) {
      return
    }

    const newInputs = inputs.copy()
    let newValue = rawValue

    // 1. Simple Boolean toggle
    if (rawValue === 'toggle') {
      const current = inputs[attribute]
      newValue = typeof current === 'boolean' ? !current : rawValue
    }

    // 2. FinishType toggle
    const finishTypeAliasMap: Record<string, FINISH_TYPE_LABELS> = {
      isFoil: FINISH_TYPE_LABELS.FOIL,
      isReverseHolo: FINISH_TYPE_LABELS.REVERSE_HOLO,
      isRainbowFoil: FINISH_TYPE_LABELS.RAINBOW_FOIL,
      isColdFoil: FINISH_TYPE_LABELS.COLD_FOIL,
    }

    if (attribute in finishTypeAliasMap) {
      const selectedLabel = finishTypeAliasMap[attribute]
      const isSelected = inputs.finishType === selectedLabel
      const nextFinishType = isSelected ? FINISH_TYPE_LABELS.REGULAR : selectedLabel

      newInputs.setAttribute('finishType', nextFinishType)

      onInputChange?.(newInputs)
      setInputs(newInputs)
      return
    }

    // 3. CountryEdition toggle
    else if (attribute === 'countryEdition') {
      const current = inputs.countryEdition
      newValue = current === rawValue ? CountryEdition.Regular : rawValue
      newInputs.setAttribute('countryEdition', newValue)
    }

    // 4. Default fallback
    else {
      newInputs.setAttribute(attribute, newValue)
    }

    onInputChange?.(newInputs)
    setInputs(newInputs)
  }

  const changeQuantity = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (Number.isInteger(Number(e.target.value))) handleChange(e)
  }

  for (const hotkey of hotkeys) {
    const [key, parameter, value] = hotkey

    let parsedValue: string | number = value
    let parsedParameter: string = parameter
    if (parameter === 'language') {
      parsedValue = CardLanguage[value as keyof typeof CardLanguage]
      parsedParameter = 'idLanguage'
    }
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useScopelessHotkeys(
      key,
      (e: KeyboardEvent) =>
        hotkeyCallback(e, () => set(parsedParameter as keyof typeof template, parsedValue)),
      [inputs, disableHotkeys, initialInputs]
    )
  }

  useScopelessHotkeys(
    'Esc',
    (e) =>
      hotkeyCallback(e, () => {
        stopEdit()
      }),
    [inputs, disableHotkeys, initialInputs]
  )

  useScopelessHotkeys(
    'Delete',
    (e) =>
      hotkeyCallback(e, () => {
        stopEdit()
      }),
    [inputs, disableHotkeys, initialInputs]
  )

  const confirmChanges = (e: KeyboardEvent, force = false) => {
    hotkeyCallback(
      e,
      () => {
        const quantity = Math.ceil(Number(inputs.quantity))
        const price = Number(inputs.price)

        if (!quantity || quantity < 1) return
        if (isNaN(price) || price <= 0) {
          enqueueSnackbar(`${t('addArticles.invalidPrice')}`, { variant: 'error' })
          return
        }

        // set listedAt date
        inputs.attributes[10] = new Date()

        const locations = [
          {
            _id: '',
            userId: '',
            ptArticleId: 0,
            quantity: quantity,
            deltaQuantity: quantity,
            name: inputs.location,
            totalPickedQuantity: 0,
            totalQuantityWithIssues: 0,
          },
        ]

        handleSave(
          new InventoryArticle({
            ...inputs,
            quantity,
            locations,
            taxationScheme: template.taxationScheme,
            buyPriceLock: inputs.buyPrice !== undefined ? true : undefined,
          })
        )

        setInputs(
          new RichArticle({
            ...initialInputs,
            idArticle: uuid(),
          })
        )
      },
      force
    )
  }

  useScopelessHotkeys('Space', (e) => confirmChanges(e), [inputs, disableHotkeys, initialInputs])

  useScopelessHotkeys('Enter', (e) => confirmChanges(e), [inputs, disableHotkeys, initialInputs])

  useScopelessHotkeys(
    'c',
    (e) =>
      hotkeyCallback(e, () => {
        const quantity = Math.ceil(Number(inputs.quantity))
        if (!quantity || quantity < 1) return

        inputs.attributes[10] = new Date()

        const locations = [
          {
            _id: '',
            userId: '',
            ptArticleId: 0,
            quantity: quantity,
            deltaQuantity: quantity,
            name: inputs.location,
            totalPickedQuantity: 0,
            totalQuantityWithIssues: 0,
          },
        ]

        handleSave(
          new InventoryArticle({
            ...inputs,
            quantity,
            price: inputs.price,
            comment: inputs.comment,
            location: inputs.location,
            locations: locations,
            idArticle: uuid(),
            taxationScheme: template.taxationScheme,
            buyPriceLock: inputs.buyPrice !== undefined ? true : undefined,
          }),
          true
        )

        const newInputs = new RichArticle({
          ...inputs.toPlainObject(),
          price: inputs.price,
          idArticle: uuid(),
        })

        setInputs(newInputs)
      }),
    [inputs, disableHotkeys, initialInputs]
  )

  if (addByExp) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useScopelessHotkeys('b', (e) => hotkeyCallback(e, () => handleSave('next')), [
      inputs,
      disableHotkeys,
    ])
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useScopelessHotkeys('v', (e) => hotkeyCallback(e, () => handleSave('previous')), [
      inputs,
      disableHotkeys,
    ])
  }

  return (
    <Box
      key={
        //forces rerender when
        disableHotkeys ? 'lol' : 'mol'
      }
    >
      <Box data-testid="article-being-added-box">
        <Box margin="0 auto" width="fit-content">
          <div data-testid={'visual-article-to-add-' + inputs.attributes[3]}>
            <VisualArticle
              displayOnly
              article={new InventoryArticle(inputs.toPlainObject())}
              currency={user?.currency}
              visualArticleSizeClass="one"
            />
          </div>
        </Box>
        <TextField
          className="mt-2"
          key={inputs.id}
          autoFocus
          inputRef={textFieldRef}
          onFocus={(e) => {
            e.target.select()
            setHasFocus(true)
          }}
          onBlur={() => setHasFocus(false)}
          value={inputs.quantity}
          onChange={changeQuantity}
          margin="none"
          label={t('card.quantity')}
          name="quantity"
          fullWidth
          disabled={gradingActive}
          InputProps={{
            inputProps: { min: 1 },
            autoComplete: 'off',
            readOnly: disabledQuantityTextfield,
          }}
          error={inputs.quantity <= 0}
          data-testid="quanty-input-add-new-article"
          helperText={
            hasFocus ? (
              t('addArticles.hotkeysActive')
            ) : (
              <span style={{ color: palette.primary.main }}>
                <b>{t('addArticles.clickFieldToEditArticle')}</b>
              </span>
            )
          }
        />
        <TextField
          value={inputs.location}
          onChange={handleChange}
          margin="none"
          label={t('card.location')}
          name="location"
          fullWidth
          data-testid="location-input-add-new-article"
        />

        <TextField
          value={inputs.comment}
          onChange={handleChange}
          margin="none"
          label={t('card.comment')}
          name="comment"
          fullWidth
          data-testid="comment-input-add-new-article"
          disabled={disabledCommentTextfield}
        />
        <Typography variant="caption">{t('addArticles.commentDescription')}</Typography>
      </Box>

      {!disableHotkeys && (
        <>
          {!isMobile ? (
            <>
              <div
                className="add-cards-overlay"
                style={{
                  zIndex: '2',
                  position: 'absolute',
                  width:
                    window.innerWidth <= 1280 ? window.innerWidth - 315 : window.innerWidth - 388,
                  height: '100vh',
                  right: '0px',
                  top: '0px',
                  backgroundColor: 'rgb(0, 0, 0, 0.8)',
                }}
              ></div>

              <Draggable
                key={draggableRerenderKey}
                onStop={(_e, data) => {
                  if (!fixedLegendPosition) {
                    localStorage.setItem('hotkey-legend-x', String(data.x))
                    localStorage.setItem('hotkey-legend-y', String(data.y))
                  }
                }}
                defaultPosition={
                  isMobile
                    ? {
                        x: window.innerWidth - 500, // centrandolo rispetto a ipotetica larghezza
                        y: window.innerHeight / 2 - 300, // centrandolo verticalmente (approssimazione)
                      }
                    : {
                        x:
                          fixedLegendPosition?.[0] ||
                          Number(localStorage.getItem('hotkey-legend-x')) ||
                          width - 300,
                        y:
                          fixedLegendPosition?.[1] ||
                          Number(localStorage.getItem('hotkey-legend-y')) ||
                          0,
                      }
                }
              >
                <Box
                  mt={2}
                  zIndex={2}
                  style={{
                    position: 'absolute',
                    backgroundColor: 'white',
                    padding: '24px',
                    borderRadius: '10px',
                    top: '10%',
                    left: '-50%',
                  }}
                >
                  <HotkeyLegend
                    article={inputs}
                    onClick={() => textFieldRef.current?.focus()}
                    addByExp={addByExp}
                    disabled={!hasFocus}
                    setInputs={setInputs}
                    showExistingStock={showExistingStock}
                    template={templateValues}
                    setDisabledCommentTextfield={setDisabledCommentTextfield}
                    onGradingModeChange={setGradingActive}
                    setArticleIsGraded={setArticleIsGraded}
                    set={set}
                    onRefocus={() => textFieldRef.current?.focus()}
                  />
                </Box>
              </Draggable>
            </>
          ) : (
            <>
              <div
                className="mobile-hotkey-container"
                style={{
                  position: 'fixed',
                  backgroundColor: 'white',
                  padding: isPhone ? '10px' : '24px',
                  borderRadius: '10px',
                  top: '3%',
                  right: isPhone ? 0 : '3%',
                  // su telefono il drawer del FilterDock occupa 60vw: l'hotkey riempie i 40vw rimanenti.
                  // Lo impaginiamo come 50vw e lo riduciamo a scale(0.8) (ancorato in alto a destra),
                  // così la resa è ~40vw ma con più tasti per riga e meno altezza.
                  width: isPhone ? '50vw' : width / 2,
                  boxSizing: 'border-box',
                  transform: isPhone ? 'scale(0.75)' : undefined,
                  transformOrigin: 'top right',
                }}
              >
                <HotkeyLegend
                  article={inputs}
                  onClick={() => textFieldRef.current?.focus()}
                  addByExp={addByExp}
                  disabled={!hasFocus}
                  setInputs={setInputs}
                  showExistingStock={showExistingStock}
                  template={templateValues}
                  setDisabledCommentTextfield={setDisabledCommentTextfield}
                  onGradingModeChange={setGradingActive}
                  setArticleIsGraded={setArticleIsGraded}
                  set={set}
                  onRefocus={() => textFieldRef.current?.focus()}
                />
              </div>
            </>
          )}
        </>
      )}
      {!fixedLegendPosition && !isMobile && (
        <Box
          position="absolute"
          borderRadius="50%"
          bottom={25}
          right={25}
          zIndex={1399}
          style={{ backgroundColor: palette.lightBackground }}
        >
          <CustomTooltip title="Reset hotkey position">
            <IconButton
              color="primary"
              onClick={() => {
                localStorage.setItem('hotkey-legend-x', String(width - 900))
                localStorage.setItem('hotkey-legend-y', String(0))
                setDraggableRerenderKey(uuid())
              }}
            >
              <RestartIcon />
            </IconButton>
          </CustomTooltip>
        </Box>
      )}
    </Box>
  )
}
